import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { toast } from "react-toastify";
import Loader from "../../UI/Loader";
import { Table, Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../store/auth-context";
import { useContext } from "react";

const GetMedPrescription = () => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchPara, setSearchPara] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [patientData, setPatientData] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]); // ✅ history list
  const [selectedCheckUp, setSelectedCheckUp] = useState(null); // ✅ selected checkup
  const [selectedMedicines, setSelectedMedicines] = useState([]); // ✅ medicines of selected checkup
  const [showMedicineModal, setShowMedicineModal] = useState(false);

  const handleSearchChange = (e) => setSearchPara(e.target.value);

  // ✅ Search Patients
  const onSearchPatientHandler = async (e) => {
    e.preventDefault();

    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const apiUrl =
        `${apiChar}/patient/getPatientByFNameOrLNameOrMobile?value=` +
        searchPara;

      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch patient data");

      const data = await response.json();
      if (!data || data.length === 0) {
        toast.info(t("No patients found"));
      }
      setPatientData(data);
    } catch (err) {
      console.error("Search error:", err);
      toast.error(t("Error fetching patients"));
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ When selecting patient → get history
  const getSelectedPatientFromList = async (patient) => {
    setSelectedPatient(patient);
    setPatientData([]); // close modal

    if (!patient?.patientId) return;

    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    const apiChar = getApiChara();
    const apiUrl = `${apiChar}/patient/getPatientHistoryWithoutPrescriptionByIdForPharamacist/`;

    try {
      setIsLoading(true); // 👈 start loader
      const response = await fetch(apiUrl + patient.patientId, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch history");

      const data = await response.json();
      setPatientHistory(data || []);
    } catch (err) {
      console.error("History fetch error:", err);
      toast.error(t("Error fetching history"));
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Fetch medicines for specific checkup
  const handleViewCheckup = async (checkUpId) => {
    const token = getAuthToken();
    if (!token || token === "EXPIRED") {
      navigate("/");
      return;
    }

    try {
      setIsLoading(true);
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/checkUp/getCheckUpRecordById?id=${checkUpId}`;

      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch checkup details");

      const data = await response.json();
      setSelectedCheckUp(data);
      setSelectedMedicines(data?.prescription || []);
      setShowMedicineModal(true);
    } catch (err) {
      console.error("Checkup fetch error:", err);
      toast.error(t("Error fetching checkup details"));
    } finally {
      setIsLoading(false);
    }
  };

  const onDataCloseHandler = () => {
    setPatientData([]);
  };

  return (
    <div className="container pt-3">
      {isLoading && <Loader />}

      {/* Patient Search */}
      <form onSubmit={onSearchPatientHandler} className="mb-4">
        <div className="row g-3 align-items-center">
          <div className="col-12 col-sm-auto">
            <label className="col-form-label fw-bold">{t("Search Patient")}</label>
          </div>
          <div className="col-12 col-sm-8 flex-grow-1">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder={t("Enter Name or Mobile")}
                value={searchPara}
                onChange={handleSearchChange}
                required
              />
              <button type="submit" className="btn px-3" style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}>
                <i className="fa fa-search"></i>
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Selected Patient Info */}
      {selectedPatient && (
        <div className="alert alert-info d-flex justify-content-between align-items-center">
          <div>
            <strong>{t("Patient Id")}:</strong> {selectedPatient.patientId}
          </div>
          <div>
            <strong>{t("Name")}:</strong>{" "}
            {[selectedPatient.firstName, selectedPatient.middleName, selectedPatient.lastName]
              .filter(Boolean)
              .join(" ")}
          </div>
        </div>
      )}

      {/* Patient History Table */}
      {patientHistory.length > 0 && (
        <div className="mt-4">
          <h5 className="mb-3">{t("Patient Prescription")}</h5>
          <Table responsive striped bordered hover size="sm">
            <thead>
              <tr>
                <th>{t("Date & Time")}</th>
                <th>{t("Patient Name")}</th>
                <th>{t("Mobile")}</th>
                <th>{t("Diagnosis")}</th>
                <th>{t("Action")}</th>
              </tr>
            </thead>
            <tbody>
              {patientHistory.map((h) => (
                <tr key={h.checkUpId}>
                  <td>{h.dateTime}</td>
                  <td>
                    {[h.patientId.firstName, h.patientId.middleName, h.patientId.lastName]
                      .filter(Boolean)
                      .join(" ")}
                  </td>
                  <td>{h.patientId.mobile}</td>
                  <td>{h.diagnosis}</td>
                  <td>
                    <Button
                      size="sm"
                      style={{
                        backgroundColor: 'transparent',
                        color: 'var(--primary-color)',
                        border: '1px solid var(--primary-color)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                      onClick={() => handleViewCheckup(h.checkUpId)}
                    >
                      <i className="fa fa-plus"></i> {t("View")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* ✅ Patient Selection Modal */}
      <Modal
        show={patientData?.length > 0}
        onHide={onDataCloseHandler}
        centered
        size="lg"
        className="patientModel"
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: "var(--primary-color)",
            color: "white",
            border: "1px solid var(--primary-color)",
          }}
        >
          <Modal.Title className="w-100 text-center">
            {t("Select Patient")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table responsive striped bordered hover>
            <thead>
              <tr>
                <th>{t("Patient Id")}</th>
                <th>{t("Name")}</th>
                <th>{t("Mobile")}</th>
                <th>{t("Action")}</th>
              </tr>
            </thead>
            <tbody>
              {patientData.map((p) => (
                <tr key={p.patientId}>
                  <td>{p.patientId}</td>
                  <td>
                    {`${p.firstName || ""} ${p.middleName ? p.middleName + " " : ""
                      }${p.lastName || ""}`.trim()}
                  </td>
                  <td>{p.mobile || "-"}</td>
                  <td>
                    <Button
                      size="sm"
                      style={{ backgroundColor: 'var(--primary-color)', border: 'none' }}
                      onClick={() => getSelectedPatientFromList(p)}
                    >
                      {t("Select")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      {/* ✅ Medicines Modal */}
      <Modal show={showMedicineModal} onHide={() => setShowMedicineModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ backgroundColor: 'var(--primary-color)', color: 'white', borderBottom: 'none' }}>
          <Modal.Title style={{ fontWeight: '600' }}>{t("Prescribed Medicines")}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '0' }}>
          {selectedMedicines?.length > 0 ? (
            <div className="table-responsive">
              <Table hover className="mb-0" style={{ fontSize: '0.9rem' }}>
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th className="py-3 px-3 uppercase fw-bold" style={{ borderBottom: '2.5px solid #dee2e6', color: '#64748b' }}>{t("Medicine Name")}</th>
                    <th className="py-3 text-center" style={{ borderBottom: '2.5px solid #dee2e6' }}>{t("Type")}</th>
                    <th className="py-3 text-center" style={{ borderBottom: '2.5px solid #dee2e6' }}>{t("Dosage")}</th>
                    <th className="py-3 text-center" style={{ borderBottom: '2.5px solid #dee2e6', backgroundColor: '#fff8e1' }}>{t("B")}</th>
                    <th className="py-3 text-center" style={{ borderBottom: '2.5px solid #dee2e6', backgroundColor: '#e3f2fd' }}>{t("L")}</th>
                    <th className="py-3 text-center" style={{ borderBottom: '2.5px solid #dee2e6', backgroundColor: '#f1f8e9' }}>{t("D")}</th>
                    <th className="py-3 text-center" style={{ borderBottom: '2.5px solid #dee2e6' }}>{t("Days")}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedMedicines.map((med, idx) => (
                    <tr key={idx}>
                      <td className="py-3 px-3 fw-bold" style={{ color: '#1e293b', minWidth: '200px' }}>{med.medicineName}</td>
                      <td className="py-3 text-center"><span className="badge rounded-pill bg-light text-dark border">{med.type || med.medicineType}</span></td>
                      <td className="py-3 text-center" style={{ fontWeight: '600' }}>{med.dosage || "-"}</td>
                      <td className="py-3 text-center" style={{ backgroundColor: '#fffde7' }}>{med.breakFastDose || "-"}</td>
                      <td className="py-3 text-center" style={{ backgroundColor: '#eef6fc' }}>{med.lunchDose || "-"}</td>
                      <td className="py-3 text-center" style={{ backgroundColor: '#f9fbf7' }}>{med.dinnerDose || "-"}</td>
                      <td className="py-3 text-center fw-bold">{med.days}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="p-5 text-center text-muted">
              <i className="fa fa-info-circle fa-3x mb-3" style={{ opacity: 0.2 }}></i>
              <p>{t("No medicines available for this visit.")}</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: 'none', padding: '15px 25px' }}>
          <Button style={{ backgroundColor: 'var(--primary-color)', border: 'none', borderRadius: '8px', padding: '10px 30px', fontWeight: '700' }} onClick={() => setShowMedicineModal(false)}>
            {t("Close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div >
  );
};

export default GetMedPrescription;
